import { GetStaticPaths, GetStaticProps } from "next";
import Image from "next/image";
import { useRouter } from "next/router";
import { ArticleJsonLd, NextSeo } from "next-seo";

import ContactForm from "../../components/ContactForm";
import Layout from "../../components/Layout";
import { GetPostQuery, Stage } from "../../interfaces";
import getCommonProps, { CommonProps } from "../../lib/getCommonProps";
import getClient from "../../lib/sdk";
import siteUrl from "../../lib/siteUrl";

type Props = {
  post: GetPostQuery["post"];
} & CommonProps;

const Post = ({ post, ...commonProps }: Props) => {
  const router = useRouter();

  if (router.isFallback) {
    return <div>Yükleniyor...</div>;
  }

  return (
    <Layout {...commonProps}>
      <NextSeo
        title={"Rayonex " + post?.title}
        description={post?.excerpt || ""}
        openGraph={{ url: "/duyurular/" + post?.slug }}
      />
      <ArticleJsonLd
        url={siteUrl + "/" + post?.slug}
        title={post?.title || ""}
        images={[
          post?.coverImage?.url || siteUrl + "/" + "logo-with-background.webp",
        ]}
        datePublished={post?.publishedAt}
        description={post?.excerpt || ""}
        publisherName="Binbircare"
        publisherLogo={siteUrl + "/" + "logo.svg"}
        authorName={["Binbircare"]}
      />
      <div className="flex flex-col items-center flex-1 px-4">
        <Image
          src={post?.coverImage?.url || "/logo-with-background.webp"}
          width={1024}
          height={576}
          alt={post?.title!}
          className="object-cover"
        />
        <div className="flex flex-col items-start flex-1 mt-10 space-y-5 lg:w-1/2">
          <h1 className="text-4xl font-bold font-title">{post?.title}</h1>
          <div className="text-sm text-gray-400">
            {new Date(post?.publishedAt).toLocaleDateString() ||
              post?.createdAt}
          </div>
          <div
            className="prose sm:prose lg:prose-lg xl:prose-xl"
            dangerouslySetInnerHTML={{ __html: post?.body?.html || "" }}
          />
        </div>

        <ContactForm />
      </div>
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const graphcms = await getClient();
  const { posts } = await graphcms.GetPosts();

  return {
    paths: posts.map((post) => {
      return { params: { slug: post?.slug } };
    }),
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  if (!(context.params?.slug && typeof context.params.slug === "string")) {
    return { notFound: true };
  }

  const commonProps = await getCommonProps(context);
  const graphcms = await getClient(context.preview);
  const { post } = await graphcms.GetPost({
    slug: context.params.slug,
    ...(context.preview && { stage: Stage.Draft }),
  });

  return { props: { post, ...commonProps } };
};

export default Post;
